import { ACCESS_KEY, urlAPIv2 } from "../../../urls";
import customFetch from "../../../api/customFetch";
import {
  transcodeImage,
  uploadStrapiFile,
} from "../../../store/actionsCreators/fileActions";
import { normalizeMediaOrientation, type MediaOrientation } from "./insiderMediaOrientation";

export type InsiderReaction =
  "like" | "love" | "fire" | "insightful" | "unsure";
export type InsiderEngagement = {
  reactions: Record<InsiderReaction, number>;
  totalReactions: number;
};
export type InsiderAdminPost = Record<string, any> & {
  id: number;
  Title: string;
  Category: string;
  Audience: string;
  PublicationType: string;
  Excerpt: string;
  PreviewOnly?: boolean;
  MediaOrientation?: MediaOrientation;
  publishedAt?: string | null;
  engagement?: InsiderEngagement;
};
export class InsiderApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly details?: any,
  ) {
    super(message);
    this.name = "InsiderApiError";
  }
}
export type InsiderPostForm = {
  Title: string;
  Category: string;
  Audience: string;
  PublicationType: string;
  Excerpt: string;
  Body: string;
  ExpiresAt?: string | null;
  PinnedPriority: number;
  Cover?: string;
  Images?: string[];
  Video?: string;
  VideoPoster?: string;
  VideoAlt?: string;
  YoutubeVideoId?: string;
  ExternalUrl?: string;
  ExternalTitle?: string;
  ExternalDescription?: string;
  ExternalImage?: string;
  CtaLabel?: string;
  CtaUrl?: string;
  PreviewOnly: boolean;
  MediaOrientation: MediaOrientation;
};
export type InsiderSerializationResult = {
  data: Record<string, unknown>;
  errors: Record<string, string>;
};

export const trimmedString = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";
const optionalString = (value: unknown): string | undefined => {
  const trimmed = trimmedString(value);
  return trimmed ? trimmed : undefined;
};

export function normalizeInsiderPostForm(
  post: Record<string, any>,
): Partial<InsiderPostForm> & { ExpiresAt: string; PinnedPriority: number } {
  const images = Array.isArray(post.Images)
    ? post.Images.map(trimmedString).filter(Boolean)
    : [];
  return {
    Title: trimmedString(post.Title),
    Category: trimmedString(post.Category),
    Audience: trimmedString(post.Audience),
    PublicationType: trimmedString(post.PublicationType),
    Excerpt: trimmedString(post.Excerpt),
    Body: trimmedString(post.Body),
    Cover: trimmedString(post.Cover),
    Images: images,
    Video: trimmedString(post.Video),
    VideoPoster: trimmedString(post.VideoPoster),
    VideoAlt: trimmedString(post.VideoAlt),
    YoutubeVideoId: trimmedString(post.YoutubeVideoId),
    ExternalUrl: trimmedString(post.ExternalUrl),
    ExternalTitle: trimmedString(post.ExternalTitle),
    ExternalDescription: trimmedString(post.ExternalDescription),
    ExternalImage: trimmedString(post.ExternalImage),
    CtaLabel: trimmedString(post.CtaLabel),
    CtaUrl: trimmedString(post.CtaUrl),
    PreviewOnly: post.PreviewOnly === true,
    MediaOrientation: normalizeMediaOrientation(post.MediaOrientation),
    ExpiresAt: trimmedString(post.ExpiresAt).slice(0, 16),
    PinnedPriority: Number.isFinite(Number(post.PinnedPriority))
      ? Number(post.PinnedPriority)
      : 0,
  };
}

export function validateInsiderPostForm(
  form: InsiderPostForm,
): Record<string, string> {
  const errors: Record<string, string> = {};
  const title = trimmedString(form.Title);
  const category = trimmedString(form.Category);
  const audience = trimmedString(form.Audience);
  const publicationType = trimmedString(form.PublicationType);
  const body = trimmedString(form.Body);
  const youtubeVideoId = trimmedString(form.YoutubeVideoId);
  const externalUrl = trimmedString(form.ExternalUrl);
  const ctaLabel = trimmedString(form.CtaLabel);
  const ctaUrl = trimmedString(form.CtaUrl);
  if (!title) errors.Title = "Title is required.";
  if (!category) errors.Category = "Category is required.";
  if (!audience) errors.Audience = "Audience is required.";
  if (!publicationType)
    errors.PublicationType = "Publication type is required.";
  if (["text", "article", "announcement"].includes(publicationType) && !body)
    errors.Body = "Body is required for this publication type.";
  if (publicationType === "youtube" && !youtubeVideoId)
    errors.YoutubeVideoId = "YouTube video ID is required.";
  if (publicationType === "external-link" && !externalUrl)
    errors.ExternalUrl = "External URL is required.";
  if (Boolean(ctaLabel) !== Boolean(ctaUrl)) {
    errors.CtaLabel = "CTA label and URL must be filled in together.";
    errors.CtaUrl = "CTA label and URL must be filled in together.";
  }
  return errors;
}

export function serializeInsiderPostForm(
  form: InsiderPostForm,
): InsiderSerializationResult {
  const data: Record<string, unknown> = {
    Title: trimmedString(form.Title),
    Category: trimmedString(form.Category),
    Audience: trimmedString(form.Audience),
    PublicationType: trimmedString(form.PublicationType),
    PinnedPriority: Number.isFinite(Number(form.PinnedPriority))
      ? Number(form.PinnedPriority)
      : 0,
    PreviewOnly: form.PreviewOnly === true,
  };
  if (["image", "native-video"].includes(form.PublicationType))
    data.MediaOrientation = normalizeMediaOrientation(form.MediaOrientation);
  const errors: Record<string, string> = {};
  const body = optionalString(form.Body);
  if (body != null) data.Body = body;
  const excerpt = optionalString(form.Excerpt);
  if (excerpt != null) data.Excerpt = excerpt;
  const expiresAt = optionalString(form.ExpiresAt);
  if (expiresAt != null) data.ExpiresAt = expiresAt;

  const ctaLabel = optionalString(form.CtaLabel);
  const ctaUrl = optionalString(form.CtaUrl);
  if ((ctaLabel == null) !== (ctaUrl == null))
    errors.CtaLabel = errors.CtaUrl =
      "CTA label and URL must be filled in together.";
  if (ctaLabel != null && ctaUrl != null) {
    data.CtaLabel = ctaLabel;
    data.CtaUrl = ctaUrl;
  }

  if (form.PublicationType === "image") {
    const images = (Array.isArray(form.Images) ? form.Images : [])
      .map(optionalString)
      .filter((url): url is string => url != null);
    if (images.length > 0) data.Images = images;
  }
  if (["text", "article", "announcement"].includes(form.PublicationType)) {
    const cover = optionalString(form.Cover);
    if (cover != null) data.Cover = cover;
  }
  if (form.PublicationType === "native-video") {
    const video = optionalString(form.Video);
    if (video != null) data.Video = video;
    const videoPoster = optionalString(form.VideoPoster);
    if (videoPoster != null) data.VideoPoster = videoPoster;
    const videoAlt = optionalString(form.VideoAlt);
    if (videoAlt != null) data.VideoAlt = videoAlt;
  }
  if (form.PublicationType === "youtube") {
    const youtubeVideoId = optionalString(form.YoutubeVideoId);
    if (youtubeVideoId != null) data.YoutubeVideoId = youtubeVideoId;
  }
  if (form.PublicationType === "external-link") {
    const externalUrl = optionalString(form.ExternalUrl);
    if (externalUrl != null) data.ExternalUrl = externalUrl;
    const externalTitle = optionalString(form.ExternalTitle);
    if (externalTitle != null) data.ExternalTitle = externalTitle;
    const externalDescription = optionalString(form.ExternalDescription);
    if (externalDescription != null)
      data.ExternalDescription = externalDescription;
    const externalImage = optionalString(form.ExternalImage);
    if (externalImage != null) data.ExternalImage = externalImage;
  }
  return { data, errors };
}

export function getInsiderErrorMessage(error: unknown): string {
  if (error instanceof InsiderApiError)
    return (
      optionalString(error.details?.description) ??
      optionalString(error.message) ??
      "Could not save Insider post."
    );
  return error instanceof Error
    ? (optionalString(error.message) ?? "Could not save Insider post.")
    : "Could not save Insider post.";
}
const headers = (): HeadersInit => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${window.localStorage.getItem(ACCESS_KEY) ?? ""}`,
});

async function request(path: string, options: RequestInit = {}): Promise<any> {
  const response = await customFetch(`${urlAPIv2}${path}`, {
    ...options,
    headers: { ...headers(), ...(options.headers ?? {}) },
  });
  const body = await response.json().catch(() => null);
  if (!response.ok)
    throw new InsiderApiError(
      body?.error?.message ?? `Request failed (${response.status})`,
      response.status,
      body?.error?.details,
    );
  return body?.data;
}

export const listInsiderPosts = async (): Promise<InsiderAdminPost[]> =>
  (await request("/bso/admin/insider/posts"))?.data ?? [];
export const saveInsiderPost = async (
  id: number | null,
  data: Record<string, unknown>,
): Promise<InsiderAdminPost> =>
  await request(
    id == null ? "/bso/admin/insider/posts" : `/bso/admin/insider/posts/${id}`,
    { method: id == null ? "POST" : "PUT", body: JSON.stringify({ data }) },
  );
export const publishInsiderPost = async (
  id: number,
  previewOnly: boolean,
): Promise<InsiderAdminPost> =>
  await request(`/bso/admin/insider/posts/${id}/publish`, {
    method: "POST",
    body: JSON.stringify({ data: { PreviewOnly: previewOnly } }),
  });
export const unpublishInsiderPost = async (
  id: number,
): Promise<InsiderAdminPost> =>
  await request(`/bso/admin/insider/posts/${id}/unpublish`, { method: "POST" });
export const deleteInsiderPost = async (id: number): Promise<void> => {
  await request(`/bso/admin/insider/posts/${id}`, { method: "DELETE" });
};

export const uploadInsiderMedia = async (
  file: File,
  postId: number | null,
  fieldName: string,
): Promise<{ url: string; name: string }> => {
  const compressibleImage = ["image/jpeg", "image/png", "image/webp"].includes(
    file.type,
  );
  const uploadFile = compressibleImage
    ? ((await transcodeImage(file)) ?? file)
    : file;
  const uploaded = await uploadStrapiFile(
    uploadFile,
    "insider-post",
    postId == null ? null : String(postId),
    fieldName,
  );
  if (typeof uploaded?.data !== "string" || !/^https:\/\//i.test(uploaded.data))
    throw new Error(
      "Upload finished, but the canonical uploader did not return an HTTPS URL.",
    );
  return { url: uploaded.data, name: uploaded.name || file.name };
};
